import React, { Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../Contexts/authContext';
import Header from './Header';
import Footer from './Footer';
import '../styles/BetaIndicator.css';

// Lazy load menus for better initial load performance
const MenuFutebol = React.lazy(() => import('./MenuFutebol'));
const MenuFutsal = React.lazy(() => import('./MenuFutsal'));
const MenuAndebol = React.lazy(() => import('./MenuAndebol'));
const MenuVoleibol = React.lazy(() => import('./MenuVoleibol'));
const MenuBasquetebol = React.lazy(() => import('./MenuBasquetebol'));

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // Verifica se o caminho atual corresponde ao esporte do usuário
  const shouldShowMenu = () => {
    if (!user) return false;

    // Show sport menus if subscription is active OR free
    if (user.subscriptionStatus !== 'active' && user.subscriptionStatus !== 'free') return false;

    const sport = user.selectedSport?.toLowerCase();

    if (sport === 'futebol' && path.startsWith('/football')) {
      return 'futebol';
    } else if (sport === 'futsal' && path.startsWith('/futsal')) {
      return 'futsal';
    } else if (sport === 'andebol' && path.startsWith('/andebol')) {
      return 'andebol';
    } else if (sport === 'voleibol' && path.startsWith('/voleibol')) {
      return 'voleibol';
    } else if (sport === 'basquetebol' && path.startsWith('/basquetebol')) {
      return 'basquetebol';
    }

    // Se o usuário está na página inicial ou outras páginas públicas mas tem um esporte selecionado
    if (sport) {
      return sport;
    }

    return false;
  };

  const currentSport = shouldShowMenu();
  const isFreeMode = user?.subscriptionStatus === 'free';

  const publicSegments = ['home', 'sports', 'features', 'contact', 'pricing', 'terms', 'privacy-policy', 'login', 'register', 'reset-password'];
  // path is /{lang}/segment or just /{lang} — check if the segment after the lang is a public one
  const pathSegments = path.split('/').filter(Boolean); // ['pt', 'pricing'] etc.
  const pageSegment = pathSegments[1] || ''; // segment after the lang prefix
  const isPublicPage = pathSegments.length <= 1 || publicSegments.some(p => pageSegment === p || pageSegment.startsWith(p + '/'));

  // Determine if the current page content should be restricted (visual block)
  const isRestricted = isFreeMode && currentSport && !isPublicPage &&
    !path.includes('club-sheet') && !path.includes('squad');

  return (
    <div>
      {/* Subscribe Banner for Free Mode */}
      {isFreeMode && (
        <div style={{
          width: '100%',
          height: '50px',
          background: 'linear-gradient(90deg, #ef4444 0%, #b91c1c 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '11px',
          fontWeight: 'bold',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          textAlign: 'center',
          lineHeight: '1.2',
          cursor: 'pointer',
          zIndex: 900,
          position: 'fixed',
          top: 0,
          left: 0,
          padding: '0 10px',
          boxSizing: 'border-box',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
        }} onClick={() => navigate(`/${i18n.language}/pricing`)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '100%' }}>
            <span style={{ marginRight: '8px', fontSize: '14px' }}>🔒</span>
            <span>{t('common.freeModeBanner')}</span>
          </div>
        </div>
      )}

      {/* Mostra o Header se não houver menu de desporto ativo (ex: não logado ou sem subscrição ativa) */}
      {!currentSport && <Header />}

      {/* Menus laterais - ajustados para descerem se houver banner */}
      <div style={{
        backgroundColor: '#1a1a1a', /* Fix white bar issue */
        minHeight: '100vh',
        position: 'relative', // Ensure positioning context for main content
        zIndex: 1100 // Ensure Menu is above everything (Overlay is 990)
      }}>
        <Suspense fallback={<div />}>
          {currentSport === 'futebol' && <MenuFutebol />}
          {currentSport === 'futsal' && <MenuFutsal />}
          {currentSport === 'andebol' && <MenuAndebol />}
          {currentSport === 'voleibol' && <MenuVoleibol />}
          {currentSport === 'basquetebol' && <MenuBasquetebol />}
        </Suspense>

        <main style={{
          marginTop: isFreeMode ? '50px' : '0',
          position: 'relative'
        }}>
          <div style={{
            opacity: isRestricted ? 0.6 : 1,
            pointerEvents: isRestricted ? 'none' : 'auto',
            filter: isRestricted ? 'grayscale(100%)' : 'none',
            transition: 'all 0.3s ease'
          }}>
            {children}
          </div>

          {/* Blocking Overlay for Restricted Pages */}
          {isRestricted && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 990, // Lower than Menu (1100) but higher than Content
              cursor: 'not-allowed',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: '20vh'
            }}>
              {/* Optional: Add a lock icon or message in the center if desired, but user asked for overlay/block */}
            </div>
          )}
        </main>
        {!currentSport && <Footer />}
      </div>
      {/* Beta Ribbon Indicator - Hidden on main landing pages as requested */}
      {!isPublicPage && (
        <div className="beta-ribbon-container">
          <div className="beta-ribbon">{t('common.betaMode')}</div>
        </div>
      )}
    </div>
  );
};

export default Layout;